/**
 * Hook: RKG vs Legacy MC comparison data for contract dashboards
 * Respects feature flag rkg_dashboard_integration
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface RkgComparisonData {
  contract_id: string;
  contract_name: string | null;
  rkg_p50: number | null;
  rkg_p80: number | null;
  rkg_p90: number | null;
  rkg_delay_prob: number | null;
  rkg_cost_overrun_prob: number | null;
  rkg_p50_cost: number | null;
  rkg_p80_cost: number | null;
  rkg_p90_cost: number | null;
  planned_days: number | null;
  planned_cost: number | null;
  rkg_adjustments: Record<string, any> | null;
  completed_at: string | null;
  divergence_level: 'aligned' | 'moderate' | 'high_divergence' | null;
}

export interface RkgBacktestMetrics {
  mape: number | null;
  brier_score: number | null;
  coverage_p80: number | null;
  sample_size: number | null;
}

export function useRkgDashboardFlag() {
  return useQuery({
    queryKey: ['rkg-dashboard-flag'],
    queryFn: async () => {
      const { data } = await supabase
        .from('rkg_feature_flags' as any)
        .select('enabled')
        .eq('feature', 'rkg_dashboard_integration')
        .eq('scope', 'global')
        .limit(1)
        .maybeSingle();
      return !!(data as any)?.enabled;
    },
    staleTime: 60_000,
  });
}

export function useRkgComparison(contractId: string | undefined) {
  const { data: flagEnabled = false } = useRkgDashboardFlag();
  const { isAdmin, isCoAdmin } = useAuth();
  const isGlobalAdmin = isAdmin || isCoAdmin;
  const rkgData = useQuery({
    queryKey: ['rkg-comparison', contractId],
    queryFn: async () => {
      const { data: run } = await supabase
        .from('rkg_mc_runs' as any)
        .select('*')
        .eq('contract_id', contractId!)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!run) return null;
      const r = run as any;

      const divergence = r.planned_days > 0
        ? Math.abs(r.p80_duration - r.planned_days) / r.planned_days
        : 0;

      return {
        contract_id: r.contract_id,
        contract_name: null,
        rkg_p50: r.p50_duration,
        rkg_p80: r.p80_duration,
        rkg_p90: r.p90_duration,
        rkg_delay_prob: r.delay_probability,
        rkg_cost_overrun_prob: r.cost_overrun_probability,
        rkg_p50_cost: r.p50_cost,
        rkg_p80_cost: r.p80_cost,
        rkg_p90_cost: r.p90_cost,
        planned_days: r.planned_days,
        planned_cost: r.planned_cost,
        rkg_adjustments: r.rkg_adjustments,
        completed_at: r.completed_at,
        divergence_level: divergence <= 0.05 ? 'aligned' : divergence <= 0.15 ? 'moderate' : 'high_divergence',
      } as RkgComparisonData;
    },
    enabled: !!contractId && flagEnabled && isGlobalAdmin,
  });

  const backtestMetrics = useQuery({
    queryKey: ['rkg-backtest-metrics'],
    queryFn: async () => {
      const { data } = await supabase
        .from('rkg_vw_model_accuracy' as any)
        .select('*')
        .limit(1)
        .maybeSingle();
      if (!data) return null;
      const d = data as any;
      return {
        mape: d.mape_duration,
        brier_score: d.brier_delay,
        coverage_p80: d.coverage_p80,
        sample_size: d.sample_size,
      } as RkgBacktestMetrics;
    },
    enabled: !!contractId && flagEnabled && isGlobalAdmin,
    staleTime: 300_000,
  });

  return {
    enabled: flagEnabled && isGlobalAdmin,
    rkgData: rkgData.data ?? null,
    backtestMetrics: backtestMetrics.data ?? null,
    isLoading: rkgData.isLoading,
    useFallback: rkgData.data?.divergence_level === 'high_divergence',
  };
}
